import React, { useState } from "react";
import CIcon from "@coreui/icons-react";
import { cilAccountLogout, cilInfo } from "@coreui/icons";
import { useAuth } from "../auth";
import { useRouter } from "next/router";

import Avatar from "./ui/Avatar";
import TopDrawer from "./ui/TopDrawer";
import { useTheme } from "../hooks/useTheme";
import { useNotifications } from "../hooks/useNotifications";
import { isDevMockSub } from "../auth/mock-auth0";

// Linear glyphs (secondary controls → linear, per the icon rule).
const SunIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);
const MoonIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);
const GearIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H8a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V8a1.65 1.65 0 0 0 1.51 1H22a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const ROW =
  "tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-lg tw:px-2 tw:py-2 tw:text-left tw:text-sm tw:cursor-pointer tw:appearance-none tw:border-0 tw:bg-transparent tw:text-text! tw:hover:glass-hover";

// Each drawer section is its own glass island (discontinuous).
const CARD = "tw:glass tw:rounded-2xl tw:p-2";

interface UserControlsProps {
  showName?: boolean;
  className?: string;
}

// One toggler — the user button (avatar, carrying the unread dot). It opens a
// single drawer whose content is two row-item columns (notifications, then
// account) separated by empty space. Opening marks notifications seen.
const UserControls = ({ showName = true, className = "" }: UserControlsProps) => {
  const { user, logout } = useAuth();
  const router = useRouter();
  const { isDark, toggle: toggleTheme } = useTheme();
  const { unseen } = useNotifications();
  const isDev = isDevMockSub(user?.sub);
  const [open, setOpen] = useState(false);
  // The notifications island can be dismissed (hidden) from the drawer. The
  // dismiss persists (sessionStorage watermark) and the island reappears only
  // when a genuinely newer notification arrives — see useNotifications.

  const userInitials = (user?.name ?? "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const openDrawer = () => {
    setOpen(true);
  };
  const go = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <>
      {/* The one toggler: the user button. */}
      <button
        type="button"
        aria-label="Menú de usuario"
        aria-expanded={open}
        onClick={openDrawer}
        className={
          "tw:flex tw:items-center tw:gap-2 tw:cursor-pointer tw:appearance-none tw:border-0 tw:bg-transparent tw:px-2 tw:py-2 tw:text-text-muted! tw:hover:text-text! " +
          className
        }
      >
        <span className="tw:relative tw:inline-flex tw:shrink-0">
          <Avatar size="tw:h-10 tw:w-10 tw:text-sm" tone="glass">{userInitials}</Avatar>
          {unseen > 0 && (
            <span
              aria-label={`${unseen} notificaciones sin leer`}
              className="tw:absolute tw:-right-0.5 tw:-top-0.5 tw:h-3 tw:w-3 tw:rounded-full tw:bg-rose-500 tw:ring-2 tw:ring-surface"
            />
          )}
        </span>
        {showName && <span className="tw:truncate tw:text-sm">{user?.name}</span>}
      </button>

      <TopDrawer open={open} onClose={() => setOpen(false)} ariaLabel="Menú de usuario">
        {/* The notifications feed now lives on its own page (the bell in the
            nav); the drawer is just the account island. */}
        <div className={CARD}>
          <button type="button" onClick={toggleTheme} className={ROW}>
            <span className="tw:flex tw:h-5 tw:w-5 tw:items-center tw:justify-center">
              {isDark ? <SunIcon /> : <MoonIcon />}
            </span>
            {isDark ? "Tema claro" : "Tema oscuro"}
          </button>
          {isDev && (
            <button type="button" onClick={go(() => router.push("/settings"))} className={ROW}>
              <span className="tw:flex tw:h-5 tw:w-5 tw:items-center tw:justify-center">
                <GearIcon />
              </span>
              Ajustes
            </button>
          )}
          <button type="button" onClick={go(() => router.push("/about"))} className={ROW}>
            <CIcon className="tw:h-5 tw:w-5" icon={cilInfo} /> Acerca de
          </button>
          <button
            type="button"
            onClick={go(() => logout())}
            className={ROW}
          >
            <CIcon className="tw:h-5 tw:w-5" icon={cilAccountLogout} /> Salir
          </button>
        </div>
      </TopDrawer>
    </>
  );
};

export default UserControls;
